import type { PortOption } from "@RequestFormV2/sections/Schedule/utils/sharedAddressFieldPaths";

/** Unique select value; many seaports have no UN/LOCODE — fall back to name (V1 behaviour). */
export function getSeaportOptionValue(option: { un_locode?: string; name: string; id: number }): string {
  return option.un_locode || option.name || String(option.id);
}

export function mapSeaportToPortOption(option: {
  id: number;
  name: string;
  un_locode?: string;
  city_name: string;
  country_name: string;
  country_code: string;
  address?: PortOption["address"];
}): PortOption {
  const portValue = getSeaportOptionValue(option);

  return {
    id: option.id,
    label: option.name,
    value: portValue,
    city_name: option.city_name,
    country_name: option.country_name,
    country_code: option.country_code,
    code: portValue,
    un_locode: option.un_locode,
    portType: "seaport",
    address: option.address,
  };
}

/** Detects when API results already represent the stored port code (avoids duplicate fallback). */
export function portMatchesStoredCode(option: PortOption, code: string): boolean {
  return (
    option.value === code ||
    option.code === code ||
    option.un_locode === code ||
    option.label === code
  );
}

/** Strict match: same port by code only — used when removing duplicates, not unrelated ports in the same city. */
export function portIsDuplicateOfStored(
  option: PortOption,
  code: string,
  selectedPortOption: PortOption | null,
): boolean {
  return (
    option.value === code ||
    option.code === code ||
    option.un_locode === code ||
    (selectedPortOption != null && option.id === selectedPortOption.id && option.id !== 0)
  );
}

function getPortOptionDedupeKey(option: PortOption): string {
  return option.value || `id:${option.id}`;
}

export function dedupePortOptionsByValue(options: PortOption[]): PortOption[] {
  const byKey = options.reduce((acc, option) => {
    const key = getPortOptionDedupeKey(option);
    const existing = acc.get(key);
    if (!existing || (existing.id === 0 && option.id !== 0)) {
      acc.set(key, option);
    }
    return acc;
  }, new Map<string, PortOption>());

  return [...byKey.values()];
}

export function buildSyntheticPortOption(
  isAirport: boolean,
  portCode: string,
  portName?: string,
): PortOption {
  if (isAirport) {
    return {
      id: 0,
      label: portName || portCode,
      value: portCode,
      city_name: portName || "",
      country_name: "",
      country_code: "",
      code: portCode,
      portType: "airport",
    };
  }

  return {
    id: 0,
    label: portName || portCode,
    value: portCode,
    city_name: portName || "",
    country_name: "",
    country_code: "",
    code: portCode,
    un_locode: portCode,
    portType: "seaport",
  };
}

export function buildPortSelectOptions({
  mapped,
  portCode,
  portName,
  selectedPortOption,
  isAirport,
}: {
  mapped: PortOption[];
  portCode?: string;
  portName?: string;
  selectedPortOption: PortOption | null;
  isAirport: boolean;
}): PortOption[] {
  const dedupedMapped = dedupePortOptionsByValue(mapped);

  if (!portCode) {
    return dedupedMapped;
  }

  if (dedupedMapped.some((option) => portMatchesStoredCode(option, portCode))) {
    return dedupedMapped;
  }

  const fallback =
    selectedPortOption?.value === portCode
      ? selectedPortOption
      : buildSyntheticPortOption(isAirport, portCode, portName);

  const withoutMatchingPort = dedupedMapped.filter(
    (option) => !portIsDuplicateOfStored(option, portCode, selectedPortOption),
  );

  return dedupePortOptionsByValue([fallback, ...withoutMatchingPort]);
}
